import { ReactNode } from 'react';
import { Icon } from '@/components/ui/Icon';
import { CarouselThumb } from '@/components/calendar/PostPopover/CarouselThumb';
import { CaptionWithReadMore } from '@/components/calendar/PostPopover/CaptionWithReadMore';
import { PostMedia } from '@/services/posts';

interface HeaderLine {
  icon: string;
  text: string;
  className?: string;
}

/** Header foreground tone: 'light' = fixed white; 'secondary' = follows tenant secondary contrast color */
export type DashboardPopoverHeaderTone = 'light' | 'secondary';

interface HeaderToneClasses {
  icon: string;
  label: string;
  lineText: string;
  lineIcon: string;
}

const HEADER_TONE_CLASSES: Record<DashboardPopoverHeaderTone, HeaderToneClasses> = {
  light: {
    icon: 'text-white',
    label: 'text-white',
    lineText: 'text-white/80',
    lineIcon: 'text-white/80',
  },
  secondary: {
    icon: 'text-secondary-contrast',
    label: 'text-secondary-contrast',
    lineText: 'text-secondary-contrast opacity-80',
    lineIcon: 'text-secondary-contrast opacity-80',
  },
};

interface DashboardPostPopoverProps {
  /** Gradient utility classes, e.g. 'from-emerald-500 to-emerald-600' */
  gradient: string;
  /** Header icon name from ICONS */
  headerIcon: string;
  /** Header label text */
  headerLabel: string;
  /** Additional header lines (calendar name, time, etc.) */
  headerLines?: HeaderLine[];
  /** Post media array for CarouselThumb */
  postMedia: PostMedia[];
  /** Post caption */
  caption: string;
  /** Section-specific content rendered after caption inside the scrollable area */
  children?: ReactNode;
  /** Action buttons rendered in a fixed footer below the scrollable area */
  footer?: ReactNode;
  /** Override outer wrapper className (otherwise defaults to existing value) */
  className?: string;
  /** When provided, renders this instead of CarouselThumb inside the same padded wrapper */
  thumbnail?: ReactNode;
  /** Header foreground tone. Use 'secondary' when the gradient is secondary-based. Default 'light'. */
  headerTone?: DashboardPopoverHeaderTone;
}

/**
 * DashboardPostPopover
 *
 * Shared popover shell used by all dashboard section cards.
 * Renders: scrollable wrapper → gradient header → CarouselThumb
 *          → CaptionWithReadMore → optional children slot.
 *
 * Pass section-specific content (channels, metrics, action buttons)
 * as `children` to keep each section's unique layout intact.
 */
export const DashboardPostPopover = ({
  gradient,
  headerIcon,
  headerLabel,
  headerLines = [],
  postMedia,
  caption,
  children,
  footer,
  className,
  thumbnail,
  headerTone = 'light',
}: DashboardPostPopoverProps) => {
  const mediaCount = postMedia?.length ?? 0;
  const firstMedia = postMedia?.[0];
  const tone = HEADER_TONE_CLASSES[headerTone];

  return (
    <div className={className ?? 'max-h-full lg:max-h-[70vh] h-full lg:h-auto flex flex-col'}>
      {/* Fixed gradient header */}
      <div className={`bg-gradient-to-r ${gradient} px-5 pr-14 lg:px-4 lg:pr-4 py-4 lg:py-3 flex-shrink-0`}>
        <div className="flex items-center justify-center lg:justify-start gap-2">
          <Icon name={headerIcon} size="sm" className={tone.icon} ariaHidden />
          <span className={`${tone.label} font-medium text-sm`}>{headerLabel}</span>
        </div>
        {headerLines.map((line, i) => (
          <div
            key={i}
            className={`${tone.lineText} text-xs mt-1 flex items-center justify-center lg:justify-start gap-1${line.className ? ` ${line.className}` : ''}`}
          >
            <Icon name={line.icon} size="xs" className={tone.lineIcon} ariaHidden />
            {line.text}
          </div>
        ))}
      </div>

      {/* Scrollable middle: carousel thumbnail + caption + children */}
      <div className="overflow-y-auto flex-1 min-h-0 scrollbar-none">
        {/* Full-width carousel thumbnail — or custom thumbnail override */}
        <div className="px-5 py-3 lg:p-[9px]">
          {thumbnail !== undefined ? (
            thumbnail
          ) : (
            <CarouselThumb
              media={postMedia || []}
              mediaCount={mediaCount}
              firstMedia={firstMedia}
            />
          )}
        </div>

        {/* Content: caption + section-specific slot */}
        <div className="px-5 lg:px-4 py-4">
          <CaptionWithReadMore caption={caption} />
          {children}
        </div>
      </div>

      {/* Fixed footer — only rendered when footer prop is provided */}
      {footer && (
        <div className="border-t border-gray-100 px-5 lg:px-4 py-4 flex-shrink-0">
          {footer}
        </div>
      )}
    </div>
  );
};
